import { useEffect, useState } from 'react';
import { primaryNavigation, socialLinks } from '../../../main/marketing-v2/marketingNavigationConfig';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Button from 'app/shared-components/marketing/Button';
import CloseIcon from '@mui/icons-material/Close'
import styled from '@emotion/styled';

const MainNavLink = styled(NavLink)`
  position: relative;

  &.active::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 3px;
    background-color: #22B721;
    pointer-events: none;
  }
`;

function HeaderLayoutMarketingV2() {
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setDrawerIsOpen(false);
    setAnchorEl(null);
    setOpenDropdown(null);
    setMobileDropdownOpen(null);
  }, [location]);

  const handleDropdownClick = (event, itemId) => {
    setAnchorEl(event.currentTarget);
    setOpenDropdown(itemId);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setOpenDropdown(null);
  };

  return (
    <>
      <header className="w-full">
        <div className="lg:container lg:px-40 lg:mx-auto">
          <div
            className="flex justify-between items-center w-full px-16 lg:px-40 py-4 lg:py-18 bg-hs-white lg:rounded-full"
          >
          <Link
            className="flex items-center flex-none"
            to="/"
          >
            <img className="h-40 lg:h-60" src="assets/images/logo/HALESPRING-LOGO-100.png" alt="logo" />
            <img className="h-40 lg:h-40" src="assets/images/logo/HALESPRING-TEXT.png" alt="halespring" />
          </Link>

          <div className="flex items-center">
            <nav className="hidden lg:block">
              <ul className="flex space-x-28 items-center">
                {primaryNavigation.map((item) => 
                  <li key={item.id}>
                    {item.type === 'dropdown' ? (
                      <>
                        <button
                          onClick={(e) => handleDropdownClick(e, item.id)}
                          className="flex items-center p-8 text-hs-blue text-18 font-medium whitespace-nowrap"
                        >
                          {item.title}
                          <ExpandMoreIcon className="ml-4" />
                        </button>
                        <Menu
                          anchorEl={anchorEl}
                          open={openDropdown === item.id}
                          onClose={handleDropdownClose}
                          PaperProps={{
                            sx: {
                              mt: 1,
                              borderRadius: '12px',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                              border: '1px solid #e5e7eb',
                              minWidth: 'fit-content',
                              width: 'auto'
                            }
                          }}
                        >
                          {item.items.map(subItem => (
                            <MenuItem 
                              key={subItem.id} 
                              component={Link} 
                              to={subItem.url} 
                              onClick={handleDropdownClose}
                              sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#1e40af',
                                py: 1.5,
                                px: 3,
                                '&:hover': {
                                  backgroundColor: '#f0f9ff',
                                  color: '#1d4ed8'
                                }
                              }}
                            >
                              {subItem.title}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      <MainNavLink
                        to={item.url}
                        className="block p-8 text-hs-blue text-18 font-medium whitespace-nowrap"
                      >
                        {item.title}
                      </MainNavLink>
                    )}
                  </li>
                )}

                <li>
                  <Button
                    to="/sign-up"
                    className="bg-hs-blue hover:bg-hs-blue-dark text-hs-white"
                    dense
                  >
                    Sign-Up
                  </Button>
                </li>

                <li>
                  <Button
                    to="/sign-in"
                    className="bg-hs-blue hover:bg-hs-blue-dark text-hs-white"
                    dense
                  >
                    Login
                  </Button>
                </li>
              </ul>
            </nav>

            <button
              className="lg:hidden flex justify-center items-center w-40 h-40 bg-hs-blue text-hs-white rounded-full"
              aria-label="Open Menu"
              onClick={() => setDrawerIsOpen(true)}
            >
              <MenuIcon />
            </button>
          </div>
          </div>
        </div>
      </header>

      <SwipeableDrawer
        anchor="right"
        open={drawerIsOpen}
        onClose={() => setDrawerIsOpen(false)}
        onOpen={() => setDrawerIsOpen(true)}
      >
        <div className="flex flex-col p-20 pt-56 h-full">
          <button
            className="absolute top-8 right-8 flex justify-center items-center w-40 h-40 bg-hs-blue text-hs-white rounded-full"
            aria-label="Close Menu"
            onClick={() => setDrawerIsOpen(false)}
          >
            <CloseIcon />
          </button>

          <nav className="flex-auto">
            <ul className="flex flex-col gap-y-36 h-full">
              {primaryNavigation.map((item) => (
                <li key={item.id}>
                  {item.type === 'dropdown' ? (
                    <div>
                      <button
                        onClick={() => setMobileDropdownOpen(mobileDropdownOpen === item.id ? null : item.id)}
                        className="flex items-center justify-between w-full text-hs-grey-black text-48 leading-none font-medium mb-16"
                      >
                        {item.title}
                        <ExpandMoreIcon 
                          className={`transition-transform duration-200 ${
                            mobileDropdownOpen === item.id ? 'rotate-180' : ''
                          }`} 
                          sx={{ fontSize: 40 }}
                        />
                      </button>
                      {mobileDropdownOpen === item.id && (
                        <ul className="ml-20 space-y-12">
                          {item.items.map(subItem => (
                            <li key={subItem.id}>
                              <Link
                                to={subItem.url}
                                className="block text-hs-grey-black text-32 leading-none font-medium"
                              >
                                {subItem.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.url}
                      className="block text-hs-grey-black text-48 leading-none font-medium"
                    >
                      {item.title}
                    </Link>
                  )}
                </li>
              ))}

              <li className="mt-auto">
                <Button
                  to="/sign-up"
                  className="bg-hs-blue hover:bg-hs-blue-dark text-hs-white"
                >
                  Provider Sign-Up
                </Button>
              </li>

              <li>
                <Button
                  to="/sign-in"
                  className="bg-hs-blue hover:bg-hs-blue-dark text-hs-white"
                >
                  Login
                </Button>
              </li>
            </ul>
          </nav>

          <ul className="flex-none flex justify-center gap-x-16 mt-40">
            {socialLinks.map((item) => 
              <li key={item.id}>
                <a
                  href={item.url}
                  target={item.target}
                >
                  <img
                    src={`assets/images/social/${item.title.toLowerCase()}-blue.svg`}
                    alt={item.title}
                    className="w-60 h-60"
                  />
                </a>
              </li>
            )}
          </ul>
        </div>
      </SwipeableDrawer>
    </>
  );
}

export default HeaderLayoutMarketingV2;